import { Page } from '@/pages/AdvancedEditor/AdvancedEditor';
import { DEFAULT_SCENE_DURATION_IN_MS } from '@/pages/AdvancedEditor/constants';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { getPageProperty, PolotnoStoreActivePageKeys, setPageProperty, usePolotnoActivePageState } from '@/pages/AdvancedEditor/hooks';
import { clearAudioCache, getNewCacheVersion } from '@/pages/AdvancedEditor/hooks/scenesPreview/scenes-preview';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { useUploadAudioFile } from '@/pages/AdvancedEditor/hooks/useUploadAudioFile';
import { isProviderSupportsSpeedControl } from '@/pages/AdvancedEditor/utils/speedLimits';
import { ScriptType } from '@d-id/types';
import styled from '@emotion/styled';
import { Tabs } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { TabButton } from '../common/TabButton';
import { AudioSection } from './AudioSection';
import { SpeedControl } from './controls/SpeedControl';
import { StylesControl } from './controls/StylesControl';
import { TextSection } from './TextSection';
import { VoiceAdvancedSettings, VoiceButton } from './Voice';

const ScriptPanelContainer = styled.div`
    padding: 0 12px 42px;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const TextContainer = styled.div<{ hasTextError: boolean }>`
    height: 100%;
    border: 1px solid ${({ theme, hasTextError }) => (hasTextError ? theme.colors.error[650] : theme.colors.black[300])};
    border-radius: 10px;
    overflow: hidden;
`;

const ErrorMessageContainer = styled.span`
    color: ${({ theme }) => theme.colors.error.main};
    font-size: 14px;
    font-weight: 400;
`;

export const TabsContainer = styled(Tabs)`
    margin: 0 16px;
`;

export const updateSceneAudio = ({ page, audioData }: { page: Page; audioData: AudioData | null }) => {
    clearAudioCache(page);

    page.set({
        duration: audioData?.duration ? audioData.duration * 1000 : DEFAULT_SCENE_DURATION_IN_MS,
        custom: {
            ...page.custom,
            [PolotnoStoreActivePageKeys.sceneDurationCalculated]: !!audioData?.duration,
            [PolotnoStoreActivePageKeys.ttsCreated]: false,
            [PolotnoStoreActivePageKeys.ttsCacheVersion]: getNewCacheVersion(page),
            [PolotnoStoreActivePageKeys.uploadedAudio]: audioData,
        },
    });
};

export const ScriptPanel = observer(() => {
    const { store } = useAdvancedEditorContext();
    const [voiceConfig, setVoiceConfig] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsVoiceConfig);
    const [ttsSelectedVoice] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsSelectedVoice);
    const [selectedScript, setSelectedScript] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.selectedScript);
    const ttsError = getPageProperty(store.activePage, PolotnoStoreActivePageKeys.ttsError);

    const [isRecordUploading, setIsRecordUploading] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.isRecordUploading);
    const [isRecording, setIsRecording] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.isRecording);
    const [uploadLoading, setUploadLoading] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.isUploadingAudio);

    const [page, setPage] = useState<Page | null>(null);

    useEffect(() => {
        setPage(store.activePage);
    }, [store.activePage]);

    const handleUpdateSceneAudio = (audioData: AudioData | null) => {
        if (!page) return;

        updateSceneAudio({ page, audioData });
    };

    const handleSetTtsAudioError = (error: string) => {
        if (!page) return;

        setPageProperty(page, PolotnoStoreActivePageKeys.ttsAudioError)(error);
    };

    const { upload: uploadAudio, isReady } = useUploadAudioFile(handleUpdateSceneAudio, handleSetTtsAudioError);

    const handleCompleteAudio = () => {
        if (!page) return;

        setPageProperty(page, PolotnoStoreActivePageKeys.textInput)('');
        setPageProperty(page, PolotnoStoreActivePageKeys.isUploadingAudio)(false);
        setPageProperty(page, PolotnoStoreActivePageKeys.isRecordUploading)(false);
    };

    const audioStateManage = {
        recordUploading: { value: isRecordUploading, set: setIsRecordUploading },
        isRecording: { value: isRecording, set: setIsRecording },
        uploadLoading: { value: uploadLoading, set: setUploadLoading },
    };

    const handleChange = (_: any, tab: ScriptType.text | ScriptType.audio) => {
        setSelectedScript(tab);
    };

    const handleStylesChange = (value: string) => {
        setVoiceConfig({ ...voiceConfig, style: value !== 'none' ? value : '' });
    };

    const handleSpeedChange = (value: string) => {
        setVoiceConfig({ ...voiceConfig, rate: value });
    };

    const { style, rate } = voiceConfig;

    const supportsSpeedControl = isProviderSupportsSpeedControl(ttsSelectedVoice?.provider);

    return (
        <ScriptPanelContainer data-testid="script-panel">
            <TextContainer hasTextError={!!ttsError && selectedScript === ScriptType.text}>
                <TabsContainer value={selectedScript} onChange={handleChange}>
                    <TabButton
                        label="Text"
                        data-testid="script-tab"
                        value={ScriptType.text}
                        disabled={isRecordUploading || isRecording || uploadLoading}
                    />
                    <TabButton label="Audio" data-testid="audio-tab" value={ScriptType.audio} />
                </TabsContainer>
                {selectedScript === ScriptType.text && <TextSection />}
                {selectedScript === ScriptType.audio && isReady && (
                    <AudioSection
                        stateManage={audioStateManage}
                        uploadAudio={uploadAudio}
                        handleComplete={handleCompleteAudio}
                        updateSceneAudio={handleUpdateSceneAudio}
                    />
                )}
            </TextContainer>

            {selectedScript === ScriptType.text && (
                <>
                    {!!ttsError && <ErrorMessageContainer>{ttsError.title}</ErrorMessageContainer>}
                    <VoiceButton ttsSelectedVoice={ttsSelectedVoice} />
                    <StylesControl ttsSelectedVoice={ttsSelectedVoice} value={style} setValue={handleStylesChange} />
                    {supportsSpeedControl && <SpeedControl ttsSelectedVoice={ttsSelectedVoice} value={rate} setValue={handleSpeedChange} />}
                    <VoiceAdvancedSettings />
                </>
            )}
        </ScriptPanelContainer>
    );
});
